import { Fragment, createContext, useContext, useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Icon,
  Avatar,
  AttentionCount,
  Badge,
  Button,
  IconButton,
  type IconName,
} from './primitives';
import { Eyebrow } from './card';
import { usePopover } from './dialog';
import { NotificationBell } from './NotificationBell';
import { BugReportModal } from './BugReportModal';
import {
  can,
  canOpen,
  roleLabel,
  WORKSPACE_MODULES,
  type ModuleGate,
  type PermMap,
} from '../lib/roles';
import { hasUnseenWhatsNew } from '../lib/whatsNew';
import { MY_SPACE } from '../lib/attention';

export type ScreenKey =
  | 'dashboard'
  | 'projects'
  | 'project'
  | 'profitability'
  | 'pipeline'
  | 'capacity'
  | 'talent'
  | 'timesheets'
  | 'gantt'
  | 'forecasting'
  | 'workspace'
  | 'hr'
  | 'contracts'
  | 'reports'
  | 'org'
  | 'me'
  | 'security'
  | 'whats-new'
  | 'org';

// No `badge` field: attention counts come from the registry (lib/attention.ts)
// keyed by module, never hardcoded per item. The old optional `badge?: number`
// was never set by a single entry in NAV_ITEMS — dead the whole time.
// A screen spanning several modules lists them all: it shows if any one opens,
// and its badge rolls up every one.
type NavItem = {
  icon: IconName;
  label: string;
  screen: ScreenKey;
  /** `null` is an account-level screen: always shown, gated by nothing. */
  module: ModuleGate | null;
  /** A further check on top of `module`, for a screen no single module gates. */
  also?: (perms: PermMap) => boolean;
};

/**
 * Project money (Profitability): people:full AND pipeline:full, owner and admin
 * in the seeded matrix. A mirror of money_overview()'s own gate, which is the
 * real one; it needs no module row of its own.
 */
// A predicate beside the nav that reads it, as screenLabel is; App's guard shares it.
// eslint-disable-next-line react-refresh/only-export-components
export const seesMoney = (perms: PermMap): boolean =>
  can(perms, 'people', 'full') && can(perms, 'pipeline', 'full');

// `module` keys MUST match role_permissions.module in the DB.
const NAV_ITEMS: NavItem[] = [
  { icon: 'home', label: 'Dashboard', screen: 'dashboard', module: 'dashboard' },
  { icon: 'briefcase', label: 'Projects', screen: 'projects', module: 'projects' },
  {
    icon: 'trending',
    label: 'Profitability',
    screen: 'profitability',
    module: 'projects',
    also: seesMoney,
  },
  { icon: 'layers', label: 'Pipeline', screen: 'pipeline', module: 'pipeline' },
  { icon: 'users', label: 'Talent', screen: 'talent', module: 'talent' },
  { icon: 'layers', label: 'Capacity', screen: 'capacity', module: 'capacity' },
  { icon: 'clock', label: 'Timesheets', screen: 'timesheets', module: 'timesheets' },
  { icon: 'gantt', label: 'Schedule', screen: 'gantt', module: 'schedule' },
  { icon: 'trending', label: 'Forecasting', screen: 'forecasting', module: 'forecasting' },
  { icon: 'inbox', label: 'Workspace', screen: 'workspace', module: WORKSPACE_MODULES },
  { icon: 'users', label: 'People', screen: 'hr', module: 'people' },
  { icon: 'list', label: 'Reports', screen: 'reports', module: 'reports' },
  { icon: 'file', label: 'Contracts', screen: 'contracts', module: 'contracts' },
  { icon: 'settings', label: 'Organisation', screen: 'org', module: 'org' },
];

// Every routable screen has a nav entry. My space had none: its only door was a
// click-only <div> around the avatar, so a keyboard user could not reach the
// page that holds the leave form, and nothing was highlighted once there
// (SH-02, ME-02, XA-03). The first group was headed "Workspace" above an item
// also called Workspace (SH-17).
//
// The "You" group (My space, Security) is gone (owner, 2026-10-09: "this thing
// is redundant, remove it and keep the things below"; bug report 2026-10-07).
// Both are the account card at the sidebar's foot — your name opens My space,
// the shield opens Security — and the account menu when the sidebar collapses.
const NAV_GROUPS: { label: string; items: NavItem[] }[] = [{ label: 'Work', items: NAV_ITEMS }];

// A row in the nav or the account menu. Its resting and hover paint is the
// ghost button's, from tokens.css (.hf-btn): an inline colour would beat the
// class and leave no hover state (UI-CONVENTIONS §4). The class's 1px
// transparent border is dropped so the row keeps its height.
const navRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--hf-space-2)',
  padding: 'var(--hf-space-2) var(--hf-space-3)',
  border: 0,
  borderRadius: 'var(--hf-radius-md)',
  fontSize: 'var(--hf-text-sm)',
  fontFamily: 'inherit',
  fontWeight: 400,
  textDecoration: 'none',
  position: 'relative',
};

// Three modes (App.tsx picks one from the width): 'wide' and the 'icon' rail
// beside the page, and 'drawer', the same nav and account actions inside the
// phone's Menu dialog, where the dialog's own header replaces the logo row and
// there is nothing to collapse.
export const Sidebar = ({
  mode = 'wide',
  currentScreen,
  theme = 'dark',
  onToggleTheme,
  onToggleSidebar,
  perms,
  roleId,
  userName,
  userTitle,
  onSignOut,
  onNavigate,
  attention = {},
}: {
  mode?: 'wide' | 'icon' | 'drawer';
  currentScreen?: ScreenKey;
  theme?: 'dark' | 'light';
  onToggleTheme?: () => void;
  onToggleSidebar?: () => void;
  perms: PermMap;
  /** Module → count of items awaiting this user's action, from useAttention().
   *  Already gated on the ability to ACT, so this component never re-checks
   *  permissions — it just renders what it is given. See docs/UI-CONVENTIONS.md. */
  attention?: Record<string, number>;
  roleId?: string;
  userName?: string | null;
  /** Job title, shown under the name in the wide account card. */
  userTitle?: string | null;
  onSignOut?: () => void;
  /** Any link in it was followed: the drawer closes, even onto the page it is
   *  already on (no change of path to hear). */
  onNavigate?: () => void;
}) => {
  const wide = mode !== 'icon';
  const drawer = mode === 'drawer';
  const otherTheme = theme === 'dark' ? 'light' : 'dark';
  // The rail has no room for the account actions, so it dropped them: signing
  // out meant expanding the sidebar first (SH-10). The avatar opens them.
  const account = usePopover({ minWidth: 200 });
  // A group with nothing in it goes, heading and all: a client used to see
  // "WORKSPACE" over an empty list.
  const groups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(
      (item) =>
        (item.module === null || canOpen(perms, item.module)) && (item.also?.(perms) ?? true),
    ),
  })).filter((g) => g.items.length > 0);

  const nav = (
    <nav
      aria-label="Main"
      className="hf-scroll"
      style={{
        flex: 1,
        padding: drawer ? 0 : 'var(--hf-space-2)',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--hf-space-3)',
      }}
    >
      {groups.map((group, g) => (
        <div
          key={group.label}
          role="group"
          aria-label={group.label}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-1)',
            // The rail has no room for the group's name; a rule keeps the
            // two groups apart.
            ...(!wide && g > 0
              ? { paddingTop: 'var(--hf-space-3)', borderTop: '1px solid var(--hf-rule)' }
              : {}),
          }}
        >
          {wide && (
            <Eyebrow as="div" style={{ padding: 'var(--hf-space-1) var(--hf-space-3)' }}>
              {group.label}
            </Eyebrow>
          )}
          {group.items.map((item) => {
            const isActive = item.screen === currentScreen;
            // Roll-up for this item's modules. Already act-gated by
            // visibleQueues(), so a module the user cannot act on has no entry.
            const badge = [item.module ?? []].flat().reduce((n, m) => n + (attention[m] ?? 0), 0);
            return (
              // A link, not a button: it has an address, so it opens in a new
              // tab and says where you are (SH-09, XA-13).
              <Link
                key={item.screen}
                to={`/${item.screen}`}
                onClick={onNavigate}
                aria-current={isActive ? 'page' : undefined}
                // The rail shows an icon and a dot. aria-label replaces the
                // link's children as its name, the dot's label included, so
                // the count goes in it: the rail must not be the one mode
                // where a pending item is silent (UI-CONVENTIONS §1).
                aria-label={
                  wide ? undefined : badge ? `${item.label}, ${badge} awaiting you` : item.label
                }
                title={wide ? undefined : item.label}
                // 44px tall under a finger, the rail's included (tokens.css).
                className="hf-btn hf-hit"
                data-variant="ghost"
                style={{
                  ...navRow,
                  // A finger's row in the drawer: 44px (Apple's 44pt).
                  ...(drawer ? { minHeight: 44 } : {}),
                  justifyContent: wide ? 'flex-start' : 'center',
                  ...(isActive
                    ? { background: 'var(--hf-surface-3)', color: 'var(--hf-ink)' }
                    : {}),
                }}
              >
                {/* The bar and the fill mark the page. No glow: a halo with no
                    offset is decoration, not depth (Design Spec §4.4). */}
                {isActive && (
                  <span
                    style={{
                      position: 'absolute',
                      left: wide && !drawer ? 'calc(-1 * var(--hf-space-2))' : 0,
                      top: 'var(--hf-space-2)',
                      bottom: 'var(--hf-space-2)',
                      width: 2,
                      background: 'var(--hf-accent)',
                      borderRadius: 'var(--hf-radius-pill)',
                    }}
                  />
                )}
                <Icon name={item.icon} size={15} />
                {wide ? (
                  <>
                    <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
                    <AttentionCount n={badge} label={`awaiting you in ${item.label}`} />
                  </>
                ) : (
                  <AttentionCount n={badge} label={`awaiting you in ${item.label}`} variant="dot" />
                )}
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );

  // Not one clickable card: the account actions sit beside the link to My
  // space, and a control cannot hold another (ME-02). A flat panel: the
  // sidebar is already the glass, and a glass card on it floated a second
  // blurred, shadowed layer over the first (UI-CONVENTIONS §8, elevation).
  const accountCard = (
    <div
      className="hf-panel"
      style={{
        padding: 'var(--hf-space-2)',
        borderRadius: 'var(--hf-radius-card)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--hf-space-1)',
      }}
    >
      <Link
        to="/me"
        onClick={onNavigate}
        title="Open My space"
        className="hf-hit"
        // The card is My space's only door now that the "You" group is gone,
        // so it says where you are, as a nav row does (SH-02).
        aria-current={currentScreen === 'me' ? 'page' : undefined}
        style={{
          background: currentScreen === 'me' ? 'var(--hf-accent-wash)' : undefined,
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--hf-space-2)',
          borderRadius: 'var(--hf-radius-md)',
          textDecoration: 'none',
        }}
      >
        <Avatar name={userName || '—'} size={28} />
        <Identity userName={userName} roleId={roleId} title={userTitle} />
        <AttentionCount n={attention[MY_SPACE] ?? 0} label="awaiting you in My space" />
      </Link>
      <Link
        to="/security"
        onClick={onNavigate}
        aria-label="Security"
        title="Security"
        className="hf-btn hf-hit"
        data-variant="ghost"
        aria-current={currentScreen === 'security' ? 'page' : undefined}
        style={{
          ...iconLink,
          background: currentScreen === 'security' ? 'var(--hf-accent-wash)' : undefined,
        }}
      >
        <Icon name="shield" size={14} />
      </Link>
      <IconButton icon="logout" label="Sign out" onClick={onSignOut} />
    </div>
  );

  // At the foot, above Light/Collapse (owner, 2026-10-12). Its "New" tag is
  // accent, not an AttentionCount: something to read is not something to do
  // (UI-CONVENTIONS §1). Read on every render; opening the page clears it.
  const onWhatsNew = currentScreen === 'whats-new';
  const whatsNewUnseen = !onWhatsNew && hasUnseenWhatsNew(perms);
  const whatsNewLink = (
    <Link
      to="/whats-new"
      onClick={onNavigate}
      aria-current={onWhatsNew ? 'page' : undefined}
      aria-label={wide ? undefined : `What’s new${whatsNewUnseen ? ', new updates' : ''}`}
      title={wide ? undefined : 'What’s new'}
      className="hf-btn hf-hit"
      data-variant="ghost"
      style={{
        ...navRow,
        ...(drawer ? { minHeight: 44 } : {}),
        justifyContent: wide ? 'flex-start' : 'center',
        // No fill on its own page (owner, 2026-10-12: "no border needed").
        ...(onWhatsNew ? { color: 'var(--hf-ink)' } : {}),
      }}
    >
      <Icon name="sparkle" size={15} />
      {wide ? (
        <>
          <span style={{ flex: 1, textAlign: 'left' }}>What’s new</span>
          {whatsNewUnseen && <Badge tone="accent">New</Badge>}
        </>
      ) : (
        whatsNewUnseen && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 6,
              height: 6,
              borderRadius: 'var(--hf-radius-pill)',
              background: 'var(--hf-accent)',
            }}
          />
        )
      )}
    </Link>
  );

  const themeButton = (
    <Button
      size="sm"
      icon={theme === 'dark' ? 'sun' : 'moon'}
      onClick={onToggleTheme}
      title={`Switch to ${otherTheme} theme`}
      style={{ flex: 1, justifyContent: 'center', ...(drawer ? { minHeight: 44 } : {}) }}
    >
      {theme === 'dark' ? 'Light' : 'Dark'}
    </Button>
  );

  // In the drawer the dialog is the frame: the nav, then the theme and the
  // account at its foot. The dialog's body scrolls them together.
  if (drawer)
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--hf-space-4)',
          minHeight: '100%',
        }}
      >
        {nav}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}>
          {whatsNewLink}
          <div style={{ display: 'flex' }}>{themeButton}</div>
          {accountCard}
        </div>
      </div>
    );

  return (
    <aside
      style={{
        width: wide ? 224 : 60,
        flexShrink: 0,
        borderRight: '1px solid var(--hf-rule)',
        background: 'var(--hf-glass)',
        backdropFilter: 'blur(24px)',
        display: 'flex',
        flexDirection: 'column',
        // No width transition. It re-laid the whole page out on every frame of
        // a Collapse — the main column's width changes with it — and
        // transform can't stand in, since the page has to reflow once either
        // way (detector layout-transition, critique 2026-10-05). Collapse is a
        // rare toggle; one snap beats 220ms of reflow.
      }}
    >
      <div
        style={{
          height: 52, // match the topbar height so the header/topbar borders align
          padding: wide ? '0 var(--hf-space-4)' : '0 var(--hf-space-2)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--hf-space-2)',
          flexShrink: 0,
          borderBottom: '1px solid var(--hf-rule)',
        }}
      >
        {wide ? (
          <>
            <img
              src={theme === 'light' ? '/brand/hyprflow-logo-dark.png' : '/brand/hyprflow-logo.png'}
              alt="Hyprflow"
              style={{ height: 17, width: 'auto', display: 'block' }}
            />
            <span
              style={{
                fontFamily: 'var(--hf-font-mono)',
                fontSize: 'var(--hf-text-2xs)',
                color: 'var(--hf-ink-3)',
                letterSpacing: '0.08em',
                borderLeft: '1px solid var(--hf-rule-2)',
                paddingLeft: 'var(--hf-space-2)',
              }}
            >
              OS
            </span>
          </>
        ) : (
          <img
            src="/brand/hyprflow-icon.png"
            alt="Hyprflow"
            style={{ width: 26, height: 26, display: 'block', margin: '0 auto' }}
          />
        )}
      </div>
      {nav}
      {wide ? (
        <div
          style={{
            padding: 'var(--hf-space-3)',
            borderTop: '1px solid var(--hf-rule)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-2)',
          }}
        >
          {whatsNewLink}
          <div style={{ display: 'flex', gap: 'var(--hf-space-2)' }}>
            {themeButton}
            <Button
              size="sm"
              icon="chevronLeft"
              onClick={onToggleSidebar}
              title="Collapse sidebar"
              style={{ flex: 1, justifyContent: 'center' }}
            >
              Collapse
            </Button>
          </div>
          {accountCard}
        </div>
      ) : (
        <div
          style={{
            padding: 'var(--hf-space-2)',
            borderTop: '1px solid var(--hf-rule)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--hf-space-2)',
          }}
        >
          {whatsNewLink}
          <IconButton
            icon="chevronRight"
            label="Expand sidebar"
            variant="secondary"
            onClick={onToggleSidebar}
          />
          <IconButton
            icon={theme === 'dark' ? 'sun' : 'moon'}
            label={`Switch to ${otherTheme} theme`}
            variant="secondary"
            onClick={onToggleTheme}
          />
          <button
            type="button"
            {...account.triggerProps}
            aria-label="Account"
            title="Account"
            className="hf-hit"
            style={{
              // The My space dot sits on the avatar's corner.
              position: 'relative',
              display: 'flex',
              background: 'transparent',
              border: 0,
              padding: 0,
              borderRadius: 'var(--hf-radius-pill)',
              cursor: 'pointer',
            }}
          >
            <Avatar name={userName || '—'} size={30} />
            <AttentionCount
              n={attention[MY_SPACE] ?? 0}
              label="awaiting you in My space"
              variant="dot"
            />
          </button>
          {account.open && (
            <div
              {...account.panelProps}
              role="group"
              aria-label="Account"
              className="hf-dialog"
              style={{
                ...account.panelProps.style,
                padding: 'var(--hf-space-1)',
                borderRadius: 'var(--hf-radius-card)',
                color: 'var(--hf-ink)',
              }}
            >
              <div style={{ display: 'flex', padding: 'var(--hf-space-2) var(--hf-space-3)' }}>
                <Identity userName={userName} roleId={roleId} />
              </div>
              {ACCOUNT_LINKS.map((a) => (
                <Link
                  key={a.to}
                  to={a.to}
                  onClick={() => account.close()}
                  className="hf-btn"
                  data-variant="ghost"
                  style={navRow}
                >
                  <Icon name={a.icon} size={15} />
                  {a.label}
                </Link>
              ))}
              <Button
                variant="ghost"
                full
                icon="logout"
                onClick={onSignOut}
                style={{ ...navRow, justifyContent: 'flex-start' }}
              >
                Sign out
              </Button>
            </div>
          )}
        </div>
      )}
    </aside>
  );
};

const ACCOUNT_LINKS = [
  { icon: 'user', label: 'My space', to: '/me' },
  { icon: 'shield', label: 'Security', to: '/security' },
] as const;

// The same 32px box as IconButton, for an icon that is a link.
const iconLink: CSSProperties = {
  width: 32,
  height: 32,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 0,
  borderRadius: 'var(--hf-radius-md)',
};

// Who is signed in: the wide footer card and the rail's account menu. The card
// leads with the job title, as the design drew it, falling back to the access
// role when none is set (design sync, 2026-10-12). The rail's menu and the My
// space header keep showing the role, so it is never out of reach.
const Identity = ({
  userName,
  roleId,
  title,
}: {
  userName?: string | null;
  roleId?: string;
  title?: string | null;
}) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <div
      style={{
        fontSize: 'var(--hf-text-sm)',
        color: 'var(--hf-ink)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {userName || 'Signed in'}
    </div>
    <div
      style={{
        fontSize: 'var(--hf-text-2xs)',
        color: 'var(--hf-ink-3)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {title?.trim() || (roleId ? roleLabel(roleId) : '')}
    </div>
  </div>
);

// The navbar heading for each screen — one per screen, equal to its sidebar
// label. A live module (People, Timesheets, Workspace, Organisation, My space,
// Security) uses that label; anything not yet a "module" in that sense
// keeps the name the old crumb chain ended on. Record<ScreenKey, …> is
// exhaustive, so this cannot silently go stale when a screen is added.
const SCREEN_TITLE: Record<ScreenKey, string> = {
  dashboard: 'Dashboard',
  projects: 'Projects',
  project: 'Projects',
  profitability: 'Profitability',
  pipeline: 'Pipeline',
  capacity: 'Capacity',
  talent: 'Talent',
  timesheets: 'Timesheets',
  gantt: 'Schedule',
  forecasting: 'Forecasting',
  workspace: 'Workspace',
  hr: 'People',
  contracts: 'Contracts',
  reports: 'Reports',
  org: 'Organisation',
  me: 'My space',
  security: 'Security',
  'whats-new': 'What’s new',
};

// The human name for a screen — also what BugReportModal shows the reporter.
export const screenLabel = (s: ScreenKey): string => SCREEN_TITLE[s] ?? s;

// ─────────────────────────────────────────────────────────────────────────────
// Page crumbs — the small grey trail after the navbar title (e.g. People →
// Asha Rao → Hours). The title itself is Topbar's job, driven by SCREEN_TITLE;
// a screen only ever owns what comes after it, and publishes that here.
// ─────────────────────────────────────────────────────────────────────────────
export type PageCrumb = { label: string; to?: string };

type PageCrumbsCtx = { crumbs: PageCrumb[]; setCrumbs: (c: PageCrumb[]) => void };
const PageCrumbsContext = createContext<PageCrumbsCtx | null>(null);

// Mounted once in App.tsx around the Topbar and the routed screens, so the
// Topbar (a sibling of whichever screen is routed) can read what that screen
// published.
export const PageCrumbsProvider = ({ children }: { children: ReactNode }) => {
  const [crumbs, setCrumbs] = useState<PageCrumb[]>([]);
  return (
    <PageCrumbsContext.Provider value={{ crumbs, setCrumbs }}>
      {children}
    </PageCrumbsContext.Provider>
  );
};

// A screen calls this with its sub-crumbs (e.g. peopleCrumbs() in
// PeopleHRScreen); it clears them on unmount, so leaving the screen doesn't
// leave its trail behind on whatever route mounts next. Keyed on a stable
// serialisation of `crumbs` rather than the array itself — a fresh array
// literal at the call site would otherwise re-fire this effect, and the
// context value (a new object every publish) is not stable to depend on
// either, so both would loop.
export const usePageCrumbs = (crumbs: PageCrumb[]) => {
  const ctx = useContext(PageCrumbsContext);
  const key = JSON.stringify(crumbs);
  useEffect(() => {
    ctx?.setCrumbs(JSON.parse(key) as PageCrumb[]);
    return () => ctx?.setCrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
};

// Was a mailto: anchor. On a machine with no OS mail handler registered — the
// default on Windows 11 — clicking it did nothing at all: no navigation, no
// dialog, no error. And when it did work it only opened a draft the reporter
// still had to send, with nothing recorded on our side either way. Now it opens
// a dialog and writes a row.
//
// The label is passed to the modal rather than imported by it: shell.tsx imports
// BugReportModal, so the modal importing screenLabel back would be a cycle.
const ReportBugButton = ({ currentScreen }: { currentScreen: ScreenKey }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Icon-only: as a labelled text button it weighed the same as the day
          clock beside it, the one control people use every day (SH-20). Its
          own glyph (the flag is a time correction), and the bell's boxed
          paint beside it rather than a borderless ghost (critique 2026-10-05 #7). */}
      <IconButton
        icon="bug"
        label="Report a bug"
        variant="secondary"
        onClick={() => setOpen(true)}
      />
      {open && (
        <BugReportModal
          screen={currentScreen}
          screenLabel={screenLabel(currentScreen)}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export const Topbar = ({
  currentScreen,
  right,
  unread,
  onUnreadChange,
  compact = false,
  onMenu,
  menuOpen = false,
}: {
  currentScreen: ScreenKey;
  right?: ReactNode;
  /** A phone (App.tsx, below md): the Menu button, the title alone, and 44px
   *  tools. The day clock moves to the bottom bar, so `right` is not given. */
  compact?: boolean;
  /** Opens the nav drawer. */
  onMenu?: () => void;
  /** Whether the drawer is open, for the Menu button's aria-expanded. */
  menuOpen?: boolean;
  /** The notifications queue from useAttention(), for the bell's pip: the same
   *  number the Workspace item and the Notifications tab render (SH-06). */
  unread?: number;
  /** attention.reload: the bell hears a change before the registry does. */
  onUnreadChange?: () => void;
}) => {
  // The title (this screen's own name) leads, followed by whatever the routed
  // screen published via usePageCrumbs — e.g. the selected person and tab on
  // People. It carries `to` unconditionally: the shared clickable rule below
  // (not the last crumb) is what turns that off when there's nothing after it.
  const subCrumbs = useContext(PageCrumbsContext)?.crumbs ?? [];
  // A phone has room for the title alone: the trail after it (a person, a tab)
  // is on the page itself, which also names them.
  const crumbs: PageCrumb[] = [
    // A project page sits under the list: "Projects › ATR-0308".
    {
      label: screenLabel(currentScreen),
      to: currentScreen === 'project' ? '/projects' : `/${currentScreen}`,
    },
    ...(compact ? [] : subCrumbs),
  ];
  // The tab's title is the screen's own name and nothing after it. Every route
  // used to share one static title, so two open tabs could not be told apart
  // (SH-09, XA-13). NOT the crumb trail: on People the sub-crumbs are a person's
  // name, and posthog-js copies document.title into every $pageview, which
  // would send full names to a third party (docs/TELEMETRY.md, "What must never
  // be sent"). Put back on unmount, so the sign-in screen is not left titled
  // after the last route.
  const pageTitle = `${screenLabel(currentScreen)} · Hyprflow`;
  useEffect(() => {
    const was = document.title;
    document.title = pageTitle;
    return () => {
      document.title = was;
    };
  }, [pageTitle]);
  return (
    <header
      style={{
        height: 52,
        borderBottom: '1px solid var(--hf-rule)',
        display: 'flex',
        alignItems: 'center',
        padding: compact ? '0 var(--hf-space-2)' : '0 20px',
        gap: compact ? 'var(--hf-space-1)' : 'var(--hf-space-4)',
        background: 'var(--hf-glass)',
        backdropFilter: 'blur(20px)',
        flexShrink: 0,
        // backdrop-filter makes this a stacking context. With z-index:auto it lands
        // in the same paint layer as the position:relative glass cards inside the
        // <main> that follows, and DOM order then puts main on top — burying any
        // popover anchored in here (the notification panel). An explicit z-index
        // lifts the whole topbar out of that tie. Portalled overlays use 500+.
        position: 'relative',
        zIndex: 20,
      }}
    >
      {compact && (
        <IconButton
          icon="list"
          label="Menu"
          size={44}
          onClick={onMenu}
          ariaHasPopup="dialog"
          ariaExpanded={menuOpen}
        />
      )}
      <nav
        aria-label="Breadcrumb"
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--hf-space-1)',
        }}
      >
        {crumbs.map((c, i) => {
          const isTitle = i === 0;
          const last = i === crumbs.length - 1;
          const clickable = Boolean(c.to) && !last;
          return (
            <Fragment key={i}>
              {isTitle ? (
                <h1
                  style={{
                    margin: 0,
                    padding: 'var(--hf-space-1) var(--hf-space-2)',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    // The prototype's Inbox title: display font, -0.02em, sized
                    // down from 22 to sit in the 52px bar.
                    fontFamily: 'var(--hf-font-display)',
                    fontSize: 20,
                    fontWeight: 400,
                    letterSpacing: '-0.02em',
                    color: 'var(--hf-ink)',
                  }}
                >
                  {clickable ? (
                    // The tint gets a little room around the title, taken back
                    // by the negative margin so the heading doesn't move.
                    <Link
                      to={c.to!}
                      className="hf-crumb"
                      style={{
                        color: 'inherit',
                        textDecoration: 'none',
                        padding: '0 var(--hf-space-1)',
                        margin: '0 calc(-1 * var(--hf-space-1))',
                      }}
                    >
                      {c.label}
                    </Link>
                  ) : (
                    c.label
                  )}
                </h1>
              ) : clickable ? (
                <Link
                  to={c.to!}
                  className="hf-crumb"
                  style={{
                    textDecoration: 'none',
                    padding: 'var(--hf-space-1) var(--hf-space-2)',
                    fontSize: 'var(--hf-text-md)',
                    display: 'inline-block',
                  }}
                >
                  {c.label}
                </Link>
              ) : (
                <span
                  style={{
                    padding: 'var(--hf-space-1) var(--hf-space-2)',
                    color: 'var(--hf-ink-3)',
                    fontSize: 'var(--hf-text-md)',
                  }}
                >
                  {c.label}
                </span>
              )}
              {!last && (
                <Icon
                  name="chevronRight"
                  size={11}
                  style={{ color: 'var(--hf-ink-3)', opacity: 0.5 }}
                />
              )}
            </Fragment>
          );
        })}
      </nav>
      {/* .hf-topbar-tools: 44px under md (tokens.css), the bell included.
          Report a bug and the bell are both boxed now, so on a phone they keep
          a hairline apart rather than share an edge. */}
      <div
        className="hf-topbar-tools"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 'var(--hf-space-1)' : 'var(--hf-space-2)',
        }}
      >
        {right}
        <ReportBugButton currentScreen={currentScreen} />
        <NotificationBell unread={unread} onUnreadChange={onUnreadChange} />
      </div>
    </header>
  );
};

// A heading for a run of cards. It was a hard <h3> straight under the Topbar's
// h1, so /timesheets and /hr skipped a level (XA-13, TS-20): h2 by default, `as`
// when it sits under another h2. Its kicker is the shared Eyebrow. It carries no
// margin: its own 12px stacked on the parent's gap, 14px above and 26px below
// (detector heading-rhythm), so the space around it is the parent's gap alone.
export const SectionHeader = ({
  title,
  kicker,
  actions,
  as: Heading = 'h2',
}: {
  title: string;
  kicker?: string;
  actions?: ReactNode;
  as?: 'h2' | 'h3' | 'h4';
}) => (
  <div
    style={{
      display: 'flex',
      // At 320px the actions go under the title, not past the edge.
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 'var(--hf-space-2) var(--hf-space-4)',
    }}
  >
    <div style={{ minWidth: 0 }}>
      {kicker && (
        <Eyebrow as="div" style={{ marginBottom: 'var(--hf-space-1)' }}>
          {kicker}
        </Eyebrow>
      )}
      <Heading
        style={{
          fontFamily: 'var(--hf-font-display)',
          fontSize: 14,
          fontWeight: 'var(--hf-weight-medium)',
          margin: 0,
          letterSpacing: '-0.01em',
          color: 'var(--hf-ink)',
        }}
      >
        {title}
      </Heading>
    </div>
    {actions && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hf-space-2)' }}>{actions}</div>
    )}
  </div>
);
